import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import Sources from '../../components/Sources';
import FAQSection from '../../components/FAQSection';
import TableOfContents from '../../components/TableOfContents';
import Callout from '../../components/Callout';
import Breadcrumbs from '../../components/Breadcrumbs';

export default function TrailerVaegtafgift() {
  const breadcrumbs = [
    { name: "Forside", path: "/" },
    { name: "Syn og udstyr", path: "/trailer-syn" },
    { name: "Vægtafgift på trailer", path: "/trailer-vaegtafgift" }
  ];

  const tocItems = [
    { id: "500-kg-graensen", title: "Den afgiftsfri 500 kg-grænse" },
    { id: "afgiftssatser-og-vaegtklasser", title: "Afgiftssatser for trailere over 500 kg" },
    { id: "opkraevning-og-ejerskifte", title: "Hvordan opkræves vægtafgiften?" },
    { id: "registreringsafgift", title: "Ingen registreringsafgift på påhængsvogne" },
    { id: "nedvejning-af-trailer", title: "Nedvejning: Sådan sparer du afgiften" },
    { id: "oekonomi-over-tid", title: "Sammenligning: Økonomi ved 500 kg vs. 750 kg" }
  ];

  const faq = [
    {
      q: "Skal man betale vægtafgift af en trailer på 500 kg?",
      a: "Nej. Ifølge lov om vægtafgift af motorkøretøjer mv. er påhængsvogne med en tilladt totalvægt på 500 kg eller derunder fuldstændigt fritaget for vægtafgift. Den årlige afgift er 0 kr."
    },
    {
      q: "Hvornår skal man betale vægtafgift af en trailer?",
      a: "Så snart trailerens registrerede tilladte totalvægt overstiger 500 kg (dvs. fra 501 kg og opefter), skal der betales vægtafgift til Motorstyrelsen. Afgiften opkræves typisk helårligt eller halvårligt afhængigt af beløbets størrelse."
    },
    {
      q: "Betales vægtafgift efter trailerens egenvægt eller tilladte totalvægt?",
      a: "Vægtafgiften beregnes altid ud fra trailerens registrerede tilladte totalvægt, som fremgår af registreringsattesten – aldrig ud fra egenvægt eller den faktiske læssede vægt."
    },
    {
      q: "Er der registreringsafgift på en trailer i Danmark?",
      a: "Nej. Der betales ingen registreringsafgift ved køb og indregistrering af påhængsvogne i Danmark, uanset hvor stor eller dyr traileren er. Du betaler kun for selve nummerpladen (590 kr.) samt eventuel årlig vægtafgift."
    },
    {
      q: "Hvad koster det at nedveje en trailer til 500 kg?",
      a: "En nedvejning kræver et registreringssyn i en synshal, som typisk koster mellem 350 og 550 kr., samt opdatering af oplysningerne i Motorregistret. Til gengæld sparer du den årlige vægtafgift fremover og slipper for syn ved fremtidige ejerskifter."
    }
  ];

  const sources = [
    { title: "Vægtafgiftsloven (LBK nr. 105 af 24/01/2022)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2022/105" },
    { title: "Motorstyrelsen: Regler for trailer og påhængsvogn", source: "motorst.dk", url: "https://motorst.dk/borger/nummerplader/trailer" }
  ];

  return (
    <>
      <SEO 
        title="Trailer vægtafgift: Priser, satser og 500 kg-reglen" 
        description="Betaler man vægtafgift på trailer? Se hvorfor trailere op til 500 kg koster 0 kr. i afgift, afgiftssatser for trailere over 500 kg og regler for opkrævning." 
        pathname="/trailer-vaegtafgift"
        faq={faq}
        breadcrumbs={breadcrumbs}
      />

      <article>
        <Breadcrumbs items={breadcrumbs} />

        <h1 className="text-[30px] sm:text-[36px] font-bold text-[#2E3A46] tracking-tight leading-tight mb-4">
          Vægtafgift på trailer: Regler, satser og fritagelse
        </h1>

        <p className="text-[18px] text-[#1A1C1F] leading-relaxed mb-6 font-medium">
          Trailere med en tilladt totalvægt på <strong>500 kg eller derunder er fuldstændigt fritaget for vægtafgift</strong> i Danmark. Det koster altså 0 kr. om året i afgift at eje en trailer på op til 500 kg.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Har traileren en tilladt totalvægt på 501 kg eller derover, opkræver Motorstyrelsen en årlig vægtafgift, der gradueres efter trailerens registrerede tilladte totalvægt. Der betales aldrig registreringsafgift af påhængsvogne i Danmark, uanset type eller værdi.
        </p>

        <TableOfContents items={tocItems} />

        <Callout title="Hovedreglen for trailerafgift">
          <ul className="m-0 p-0 list-none space-y-1 font-mono text-[15px]">
            <li>• Tilladt totalvægt &le; 500 kg: <strong>0 kr. i årlig vægtafgift</strong></li>
            <li>• Tilladt totalvægt &gt; 500 kg: <strong>Vægtafgift opkræves løbende</strong></li>
            <li>• Registreringsafgift ved køb: <strong>0 kr. for samtlige påhængsvogne</strong></li>
          </ul>
        </Callout>

        <h2 id="500-kg-graensen" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Den afgiftsfri 500 kg-grænse
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          I Danmark er 500 kg-grænsen årsagen til, at et meget stort antal almindelige fritidstrailere og havevogne er typegodkendt og indregistreret til præcis 500 kg i tilladt totalvægt.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Når en trailer er registreret med en tilladt totalvægt på højst 500 kg, opnår du tre væsentlige fordele i henhold til dansk lovgivning:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li>Du betaler <strong>0 kr. i årlig vægtafgift</strong> til statskassen.</li>
          <li>Traileren er <strong>fritaget for periodisk syn og syn ved ejerskifte</strong>, så længe den anvendes til privat godstransport (se <Link to="/trailer-syn" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">syn af trailer</Link>).</li>
          <li>Den må altid trækkes af enhver bil med et almindeligt kategori B-kørekort, forudsat bilens anhængertræk er godkendt til vægten. Læs mere i vores oversigt over <Link to="/lille-trailer" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">lille trailer (350–500 kg)</Link>.</li>
        </ul>

        <h2 id="afgiftssatser-og-vaegtklasser" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Afgiftssatser for trailere over 500 kg
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Så snart en påhængsvogn overskrider 500 kg i tilladt totalvægt, indtræder den almindelige afgiftspligt i henhold til vægtafgiftslovens § 2. Afgiften fastsættes i faste vægtintervaller:
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Tilladt totalvægt</th>
                <th className="py-2.5 pr-4 font-semibold">Vægtafgift (årlig sats)</th>
                <th className="py-2.5 font-semibold">Opkrævningsperiode og bemærkning</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">0 – 500 kg</td>
                <td className="py-3 pr-4 font-bold text-[#B58200]">0 kr.</td>
                <td className="py-3 text-[#5C636B]">Lovbestemt afgiftsfritagelse</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">501 – 1.000 kg</td>
                <td className="py-3 pr-4 font-semibold text-[#2E3A46]">ca. 160 – 260 kr.</td>
                <td className="py-3 text-[#5C636B]">Opkræves som ét årligt beløb</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">1.001 – 2.000 kg</td>
                <td className="py-3 pr-4 font-semibold text-[#2E3A46]">ca. 370 – 580 kr.</td>
                <td className="py-3 text-[#5C636B]">Opkræves typisk i to halvårlige rater</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">2.001 – 3.000 kg</td>
                <td className="py-3 pr-4 font-semibold text-[#2E3A46]">ca. 680 – 980 kr.</td>
                <td className="py-3 text-[#5C636B]">Halvårlig opkrævning via Digital Post</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">3.001 – 3.500 kg</td>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">ca. 1.050 – 1.450 kr.</td>
                <td className="py-3 text-[#5C636B]">Maksimale vægtklasse for personbilstrailere</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Satserne reguleres årligt efter gældende finanslovssatser. Hvis du er i tvivl om den præcise afgift på dit køretøj, kan du logge ind i Motorregistret på skat.dk med MitID, hvor den gældende opkrævning fremgår under køretøjets detaljer.
        </p>

        <h2 id="opkraevning-og-ejerskifte" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Hvordan opkræves vægtafgiften?
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Vægtafgiften opkræves automatisk af Skatteforvaltningen. Opkrævningen sendes til den person eller virksomhed, der er registreret som primær ejer i Motorregistret på forfaldsdatoen.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Ved køb og salg er det vigtigt at være opmærksom på følgende administrative forhold:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li><strong>Hæftelse indtil omregistrering:</strong> Sælger hæfter for vægtafgiften, indtil køberen har omregistreret traileren i sit eget navn. Derfor bør omregistrering altid foretages straks ved overdragelsen.</li>
          <li><strong>Afmelding standser afgiften:</strong> Afgiftspligten stopper samme dag, som trailerens nummerplade afleveres hos en autoriseret nummerpladeoperatør eller i et motorcenter.</li>
          <li><strong>Forholdsmæssig tilbagebetaling:</strong> Har du forudbetalt vægtafgift for en periode, modregnes eller tilbagebetales det overskydende beløb automatisk via NemKonto ved afmelding.</li>
        </ul>

        <h2 id="registreringsafgift" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Ingen registreringsafgift på påhængsvogne
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          I Danmark er registreringsafgiften på motorkøretøjer berygtet for at være høj, men påhængsvogne er fuldstændig undtaget. Registreringsafgiftsloven omfatter udelukkende motordrevne køretøjer som biler og motorcykler.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Uanset om du køber en lille havevogn til et par tusinde kroner eller en professionel maskintrailer til 150.000 kr., betaler du alene almindelig købsmoms samt det faste statslige gebyr for en nummerplade på 590 kr. Læs mere om nummerplader og gebyrer i <Link to="/trailer-nummerplade" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer nummerplade</Link>.
        </p>

        <h2 id="nedvejning-af-trailer" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Nedvejning: Sådan sparer du afgiften
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Mange trailer-ejere vælger at nedveje en trailer med en oprindelig totalvægt på 750 kg til netop 500 kg. Formålet er at opnå den permanente afgiftsfritagelse og samtidig slippe for synskrav ved senere videresalg.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Forløbet ved en nedvejning foregår således:
        </p>

        <ol className="list-decimal list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li>Du bestiller tid til et registreringssyn i en synshal og oplyser, at du ønsker traileren nedvejet til 500 kg.</li>
          <li>Synsassistenten kontrollerer trailerens generelle stand og udsteder en ny godkendelseserklæring med den ændrede tilladte totalvægt.</li>
          <li>Den nye vægt indtastes i Motorregistret, hvorefter Motorstyrelsen automatisk sletter fremtidige opkrævninger af vægtafgift.</li>
        </ol>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Den store afvejning ved nedvejning er trailerens nyttelast. Hvis en trailer har en egenvægt på 180 kg og nedvejes fra 750 kg til 500 kg, falder dens lovlige lasteevne fra 570 kg til 320 kg. Overskrider du den nye grænse, er der tale om ulovlig overbelæsning. Se beregninger af lasteevne i <Link to="/trailer-totalvaegt" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer totalvægt</Link>.
        </p>

        <h2 id="oekonomi-over-tid" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Sammenligning: Økonomi ved 500 kg vs. 750 kg
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Nedenfor ses en samlet opgørelse over de faste offentlige afgifter og synsomkostninger over en 5-årig og 10-årig ejerperiode for henholdsvis en 500 kg trailer og en 750 kg trailer:
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Omkostningselement</th>
                <th className="py-2.5 pr-4 font-semibold">500 kg trailer</th>
                <th className="py-2.5 font-semibold">750 kg trailer</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4 font-medium text-[#2E3A46]">Nummerplade ved nykøb</td>
                <td className="py-3 pr-4">590 kr.</td>
                <td className="py-3">590 kr.</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-medium text-[#2E3A46]">Årlig vægtafgift</td>
                <td className="py-3 pr-4 font-bold text-[#B58200]">0 kr.</td>
                <td className="py-3">ca. 210 kr./år</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-medium text-[#2E3A46]">Samlet vægtafgift over 5 år</td>
                <td className="py-3 pr-4 font-bold text-[#B58200]">0 kr.</td>
                <td className="py-3">ca. 1.050 kr.</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-medium text-[#2E3A46]">Samlet vægtafgift over 10 år</td>
                <td className="py-3 pr-4 font-bold text-[#B58200]">0 kr.</td>
                <td className="py-3">ca. 2.100 kr.</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-medium text-[#2E3A46]">Syn ved videresalg (&gt; 5 år gammel)</td>
                <td className="py-3 pr-4 text-[#5C636B]">Intet synskrav</td>
                <td className="py-3">Synspligtig (ca. 450 kr.)</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Har du udelukkende brug for at køre haveaffald, papkasser og mindre byggematerialer til genbrugspladsen, er en 500 kg trailer derfor det mest økonomiske valg uden løbende driftsafgifter til staten. Læs også vores gennemgang af regler for <Link to="/koerekort-og-trailer" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">kørekort og trailer</Link>.
        </p>

        <FAQSection items={faq} />
        <Sources links={sources} />
      </article>
    </>
  );
}
